import React, { useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "../../hooks/useAuth";
import { useMutation } from "react-query";
import { fetchActionsUtil } from "../../utils/helpers";
import { useFormik } from "formik";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Certification type name is required")
    .min(2, "Name must be at least 2 characters"),
  description: Yup.string(),
});

function CertificationTypeModal({
  show,
  onHide,
  onSuccess,
  mode = "create",
  certType = null,
  refetch,
}) {
  const { backendUrl, token } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create mutation
  const createMutation = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/partnership/certification-types`,
        "POST",
        token,
        payload,
      ),
    {
      onSuccess: ({ data }) => {
        toast.success(
          data?.message || "Certification type created successfully",
        );
        if (refetch) refetch();
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to create certification type");
      },
    },
  );

  // Update mutation
  const updateMutation = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/partnership/certification-types/${certType?.id}`,
        "PUT",
        token,
        payload,
      ),
    {
      onSuccess: ({ data }) => {
        toast.success(
          data?.message || "Certification type updated successfully",
        );
        if (refetch) refetch();
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(error?.message || "Failed to update certification type");
      },
    },
  );

  const formik = useFormik({
    initialValues: {
      name: certType?.name || "",
      description: certType?.description || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        if (mode === "create") {
          createMutation.mutate(values);
        } else {
          updateMutation.mutate(values);
        }
      } catch (error) {
        toast.error("Error processing form");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onHide();
  };

  const isLoading =
    isSubmitting || createMutation.isLoading || updateMutation.isLoading;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as={"h6"}>
          {mode === "create"
            ? "Create Certification Type"
            : "Edit Certification Type"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              placeholder="Enter certification type name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.name && !!formik.errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              placeholder="Enter certification type description (optional)"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.description && !!formik.errors.description
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.description}
            </Form.Control.Feedback>
          </Form.Group>

          {Object.keys(formik.errors).length > 0 && formik.isSubmitting && (
            <Alert variant="danger">
              Please fix the errors above before submitting.
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {mode === "create" ? "Creating..." : "Updating..."}
              </>
            ) : mode === "create" ? (
              "Create"
            ) : (
              "Update"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default CertificationTypeModal;
